import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Phone,
  ArrowRight,
  Target,
  Heart,
  Users,
  Award,
  Shield,
  Leaf,
  Clock,
  CheckCircle2,
  Star,
} from "lucide-react";

const About = () => {
  const values = [
    {
      icon: Heart,
      title: "Customer First",
      description: "Your satisfaction is our top priority. We go above and beyond to exceed expectations.",
    },
    {
      icon: Shield,
      title: "Trust & Integrity",
      description: "Transparent pricing, honest communication, and reliable service you can count on.",
    },
    {
      icon: Leaf,
      title: "Eco-Conscious",
      description: "We use environmentally friendly products that are safe for your family and pets.",
    },
    {
      icon: Award,
      title: "Excellence",
      description: "High standards and attention to detail in everything we do.",
    },
  ];

  const team = [
    {
      role: "Cleaning Specialists",
      count: "25+",
      description: "Trained and certified professionals",
    },
    {
      role: "Quality Supervisors",
      count: "5",
      description: "Ensuring consistent excellence",
    },
    {
      role: "Customer Support",
      count: "4",
      description: "Available when you need us",
    },
  ];

  const milestones = [
    { year: "2014", event: "CartNexa founded in Atlanta" },
    { year: "2016", event: "Expanded to 10+ team members" },
    { year: "2018", event: "Introduced eco-friendly product line" },
    { year: "2020", event: "Launched commercial cleaning division" },
    { year: "2022", event: "Reached 500+ monthly cleanings" },
    { year: "2024", event: "Awarded Best Local Cleaning Service" },
  ];

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-primary/5 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm font-bold text-primary uppercase tracking-wider">About CartNexa</span>
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-secondary mt-4">
                Atlanta's Trusted Cleaning Partner Since 2014
              </h1>
              <p className="text-lg text-slate-600 mt-6 leading-relaxed">
                For over a decade, CartNexa has been transforming homes and offices across Atlanta. What started as a small family business has grown into one of the most trusted cleaning services in Georgia.
              </p>
              <div className="flex flex-wrap gap-8 mt-8">
                <div>
                  <p className="text-4xl font-bold text-primary">10+</p>
                  <p className="text-slate-600">Years Experience</p>
                </div>
                <div>
                  <p className="text-4xl font-bold text-primary">500+</p>
                  <p className="text-slate-600">Monthly Cleanings</p>
                </div>
                <div>
                  <p className="text-4xl font-bold text-primary">4.9</p>
                  <p className="text-slate-600">Star Rating</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1585945148306-db646373834d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                alt="Happy family in clean home"
                className="rounded-3xl shadow-xl w-full h-[400px] object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-sm font-bold text-primary uppercase tracking-wider">Our Story</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mt-4">
              From Humble Beginnings to Atlanta's Best
            </h2>
            <div className="mt-8 text-left space-y-6 text-slate-600 leading-relaxed">
              <p>
                CartNexa was founded in 2014 with a simple mission: to provide Atlanta families with cleaning services they could trust. Our founders, lifelong Atlanta residents, saw a gap in the market for professional, reliable, and eco-friendly cleaning services.
              </p>
              <p>
                Starting with just two team members and a commitment to excellence, we've grown into a full-service cleaning company serving hundreds of homes and businesses across the greater Atlanta area. Our growth has been built on word-of-mouth recommendations from satisfied customers who appreciate our attention to detail and genuine care.
              </p>
              <p>
                Today, CartNexa employs over 25 cleaning professionals, each thoroughly vetted, trained, and committed to our high standards. We've cleaned thousands of homes and continue to innovate with eco-friendly products and practices that are safe for families, pets, and the environment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-sm font-bold text-primary uppercase tracking-wider">Our Values</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mt-4">
              What Drives Us Every Day
            </h2>
          </div>

          {/* Mission Statement */}
          <Card className="bg-primary text-white rounded-3xl mb-16 overflow-hidden">
            <CardContent className="p-12 text-center">
              <Target className="w-12 h-12 mx-auto mb-6 opacity-80" />
              <h3 className="font-heading text-2xl font-bold mb-4">Our Mission</h3>
              <p className="text-xl text-white/90 max-w-2xl mx-auto leading-relaxed">
                "To provide exceptional cleaning services that give our customers the gift of time, a healthier home, and peace of mind—while treating our team like family and our planet with respect."
              </p>
            </CardContent>
          </Card>

          {/* Values Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card key={index} className="bg-white border-slate-100 rounded-2xl card-hover" data-testid={`value-card-${index}`}>
                <CardContent className="p-8 text-center">
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
                    <value.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-secondary mb-3">
                    {value.title}
                  </h3>
                  <p className="text-slate-600 text-sm">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Team */}
      <section className="py-20 md:py-28 bg-white" data-testid="team-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm font-bold text-primary uppercase tracking-wider">Our Team</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mt-4">
                Professional, Vetted & Caring
              </h2>
              <p className="text-slate-600 mt-4 mb-8 leading-relaxed">
                Every CartNexa team member goes through a rigorous hiring process including background checks, reference verification, and extensive training. We hire people who share our values and take pride in their work.
              </p>

              <div className="space-y-6">
                {team.map((member, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center">
                      <span className="text-2xl font-bold text-primary">{member.count}</span>
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-secondary">{member.role}</h3>
                      <p className="text-slate-600 text-sm">{member.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 space-y-3">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                  <span className="text-slate-700">Background-checked staff</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                  <span className="text-slate-700">Fully insured and bonded</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                  <span className="text-slate-700">Ongoing training programs</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1729774091667-ff78f4da40f7?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                alt="Professional cleaning team"
                className="rounded-3xl shadow-xl w-full h-[500px] object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sm font-bold text-primary uppercase tracking-wider">Our Journey</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mt-4">
              A Decade of Excellence
            </h2>
          </div>

          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-primary/20"></div>
            <div className="space-y-8">
              {milestones.map((milestone, index) => (
                <div key={index} className="relative flex gap-6 pl-8">
                  <div className="absolute left-6 -translate-x-1/2 w-4 h-4 rounded-full bg-primary border-4 border-white shadow"></div>
                  <div className="bg-white rounded-2xl p-6 shadow-sm flex-1">
                    <span className="text-primary font-bold">{milestone.year}</span>
                    <p className="text-slate-700 mt-1">{milestone.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-primary to-primary/80 rounded-3xl p-12 text-white text-center">
            <Award className="w-16 h-16 mx-auto mb-6 opacity-80" />
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              Our Commitment to You
            </h2>
            <p className="text-xl text-white/90 max-w-2xl mx-auto mb-8">
              We stand behind every cleaning with our 100% satisfaction guarantee. If you're not completely happy, we'll re-clean at no extra charge.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/#quote-form">
                <Button className="h-14 px-8 bg-white text-primary hover:bg-white/90 font-bold rounded-full text-lg" data-testid="about-cta-quote-btn">
                  Get Your Free Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:+18717871311">
                <Button variant="outline" className="h-14 px-8 border-2 border-white text-white font-bold rounded-full text-lg hover:bg-white hover:text-primary">
                  <Phone className="mr-2 w-5 h-5" />
                  +1-871-7871311
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
